import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';

import AutosizeInput from 'react-input-autosize';
import { unSlugifyAndCapitalise, emboldenSearchTerm } from 'utils';
import Checkbox from './Checkbox';

import styles from './PillSearch.module.scss';

interface Props {
  values: string[];
  allOptions: string[];
  handleChange: (value: string[]) => void;
}

const PillSearch: React.FC<Props> = ({ values, allOptions, handleChange }) => {
  const inputRef = useRef<HTMLInputElement | null>();
  const outerRef = useRef<HTMLDivElement | null>();

  const [textValue, setTextValue] = useState('');
  const [hoveredRowIndex, setHoveredRowIndex] = useState(0);
  const [isInputFocused, setIsInputFocused] = useState(false);

  const trimmedTextValue = useMemo((): string => {
    return textValue.trim().toLowerCase();
  }, [textValue]);

  const handleFocusInput = (): void => {
    inputRef.current!.focus(); // eslint-disable-line @typescript-eslint/no-non-null-assertion
  };

  const toggleOption = useCallback(
    (option: string, clearInput?: boolean) => {
      handleFocusInput();

      if (clearInput) {
        setTextValue('');
        setHoveredRowIndex(0);
      }

      const _values = [...values];

      const valueIndex = _values.indexOf(option);

      if (valueIndex === -1) {
        _values.push(option);
      } else {
        _values.splice(valueIndex, 1);
      }

      handleChange(_values);
    },
    [values, handleChange]
  );

  const matchingOptions = useMemo((): string[] => {
    if (!trimmedTextValue) {
      return allOptions;
    }

    return allOptions.filter((option) => option.toLowerCase().includes(trimmedTextValue));
  }, [allOptions, trimmedTextValue]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent): void => {
      if (outerRef.current && !outerRef.current.contains(e.target as Node)) {
        setIsInputFocused(false);
      }
    };

    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, []);

  useEffect(() => {
    const activeRow = matchingOptions[hoveredRowIndex];

    if (!activeRow) {
      setHoveredRowIndex(0);
    }
  }, [matchingOptions, hoveredRowIndex]);

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (matchingOptions.length) {
      let newHoveredRow = hoveredRowIndex;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          if (newHoveredRow === matchingOptions.length - 1) {
            newHoveredRow = 0;
          } else {
            newHoveredRow += 1;
          }
          break;
        case 'ArrowUp':
          e.preventDefault();
          if (!newHoveredRow) {
            newHoveredRow = matchingOptions.length - 1;
          } else {
            newHoveredRow -= 1;
          }
          break;
        case 'Enter':
          toggleOption(matchingOptions[hoveredRowIndex], true);
          break;
        case 'Backspace':
          if (!textValue && values.length) {
            toggleOption(values[values.length - 1]);
          }
          break;
        default:
          break;
      }

      if (newHoveredRow !== hoveredRowIndex) {
        setHoveredRowIndex(newHoveredRow);
      }
    }
  };

  return (
    <div ref={(ref) => (outerRef.current = ref)}>
      <div className={styles.Top} data-testid="pill-search-top">
        <button
          data-testid="pill-search-focus-button"
          type="button"
          className={styles.FocusButton}
          onClick={handleFocusInput}
        >
          Focus text input
        </button>
        <ul>
          {values.map((value) => {
            return (
              <li key={value} data-testid="general-dietary-item">
                {unSlugifyAndCapitalise(value)}
                <button
                  aria-label={`Add/remove ${value} from list`}
                  data-testid="pill-search-value"
                  type="button"
                  onClick={() => toggleOption(value)}
                >
                  <i className="mm-times" />
                </button>
              </li>
            );
          })}
        </ul>
        <AutosizeInput
          value={textValue}
          onChange={(e) => setTextValue(e.target.value)}
          onKeyDown={handleKeyPress}
          inputRef={(ref) => (inputRef.current = ref)}
          placeholder={values.length ? '' : 'Search tags...'}
          onFocus={() => setIsInputFocused(true)}
        />
      </div>
      <ul className={`${styles.Dropdown} ${matchingOptions.length && isInputFocused ? styles.Open : ''}`}>
        {matchingOptions.map((option, index) => {
          const isChecked = values.indexOf(option) !== -1;
          const isHovered = hoveredRowIndex === index;

          const formattedOption = unSlugifyAndCapitalise(option);

          const termJsx = emboldenSearchTerm(formattedOption, trimmedTextValue);

          return (
            <li key={option}>
              <button
                onMouseEnter={() => setHoveredRowIndex(index)}
                className={`${isChecked && styles.Checked} ${isHovered && styles.Hovered}`}
                type="button"
                onClick={() => toggleOption(option, true)}
                data-testid="pill-search-option"
              >
                <Checkbox isChecked={isChecked} />
                {termJsx}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default PillSearch;
